/**
 * Bagagem — estado local + outbox.
 *
 * O que está no aparelho vence a rede (mesma regra do roteiro): a lista
 * carrega do IndexedDB, depois do servidor se não havia nada; toda mutação
 * é otimista e vai para o outbox. Marcar/desmarcar não tem versão — vale o
 * último; renomear/excluir têm `baseVersion` e caem na folha de conflito.
 */
import { type BaggageCategory, type BaggageScope, uuidv7 } from '@nv/core';
import { create } from 'zustand';

import { localDb } from '../roteiro/persistence';

export interface ItemDaMala {
  id: string;
  scope: BaggageScope;
  familyId: string | null;
  title: string;
  category: BaggageCategory;
  packedAt: string | null;
  packedByMemberId: string | null;
  createdByMemberId: string;
  assigneeMemberId: string | null;
  sortOrder: number;
  version: number;
  canEdit: boolean;
}

export interface EstadoDaMala {
  tripId: string;
  meuMemberId: string;
  minhaFamiliaId: string | null;
  meuPapel: 'owner' | 'admin' | 'collaborator' | 'participant';
  items: ItemDaMala[];
}

interface Store {
  tripId: string | null;
  estado: EstadoDaMala | null;
  carregando: boolean;
  erro: boolean;
  carregar: (tripId: string) => Promise<void>;
  adicionar: (params: { scope: BaggageScope; titulos: string[]; category: BaggageCategory; assigneeMemberId?: string | null }) => Promise<void>;
  marcar: (itemId: string, packed: boolean) => Promise<void>;
  editar: (itemId: string, patch: { title?: string; category?: BaggageCategory; assigneeMemberId?: string | null }) => Promise<void>;
  excluir: (itemId: string) => Promise<void>;
  prepararVolta: (scope: BaggageScope) => Promise<void>;
}

const chave = (tripId: string) => `baggage:${tripId}`;

async function lerDoServidor(tripId: string): Promise<EstadoDaMala> {
  const res = await fetch(`/api/v1/trips/${tripId}/baggage`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar a bagagem (${res.status})`);
  const corpo = (await res.json()) as { me: { memberId: string; familyId: string | null; role: EstadoDaMala['meuPapel'] }; items: ItemDaMala[] };
  return { tripId, meuMemberId: corpo.me.memberId, minhaFamiliaId: corpo.me.familyId, meuPapel: corpo.me.role, items: corpo.items };
}

export const useBagagem = create<Store>((set, get) => {
  async function guardar(estado: EstadoDaMala) {
    set({ estado });
    const db = await localDb();
    await db.cache.set(chave(estado.tripId), estado);
  }

  async function enfileirar(operation: 'create' | 'update' | 'delete', payload: unknown, baseVersion?: number) {
    const db = await localDb();
    await db.outbox.put({
      mutationId: uuidv7(),
      entity: 'baggage_item',
      operation,
      payload,
      ...(baseVersion === undefined ? {} : { baseVersion }),
      createdAt: Date.now(),
      retryCount: 0,
      status: 'pending',
    });
    window.dispatchEvent(new Event('nv:outbox-changed'));
  }

  return {
    tripId: null,
    estado: null,
    carregando: false,
    erro: false,

    carregar: async (tripId) => {
      set({ tripId, carregando: true, erro: false });
      const db = await localDb();
      const guardado = await db.cache.get<EstadoDaMala>(chave(tripId)).catch(() => null);
      if (guardado && guardado.tripId === tripId) set({ estado: guardado });
      try {
        const doServidor = await lerDoServidor(tripId);
        // O aparelho vence a rede — inclusive durante o carregamento: a
        // pessoa pode ter adicionado um item enquanto a resposta viajava.
        // Sem pendências, o servidor é a verdade; com pendências, o que está
        // aqui fica, e o que só o servidor tem entra.
        const pendentes = (await db.outbox.list()).some((m) => m.entity === 'baggage_item');
        const atual = get().estado?.tripId === tripId ? get().estado : guardado;
        if (!pendentes || !atual) {
          await guardar(doServidor);
        } else {
          const locais = new Map(atual.items.map((i) => [i.id, i]));
          const doServidorIds = new Set(doServidor.items.map((i) => i.id));
          const mesclados = [
            ...doServidor.items.map((i) => locais.get(i.id) ?? i),
            ...atual.items.filter((i) => !doServidorIds.has(i.id)),
          ];
          await guardar({ ...doServidor, items: mesclados });
        }
        set({ carregando: false });
      } catch {
        set({ carregando: false, erro: !guardado });
      }
    },

    adicionar: async ({ scope, titulos, category, assigneeMemberId }) => {
      const e = get().estado;
      if (!e) return;
      const base = e.items.reduce((m, i) => Math.max(m, i.sortOrder), 0);
      const novos: ItemDaMala[] = titulos.map((title, k) => ({
        id: uuidv7(),
        scope,
        familyId: scope === 'family' ? e.minhaFamiliaId : null,
        title,
        category,
        packedAt: null,
        packedByMemberId: null,
        createdByMemberId: e.meuMemberId,
        assigneeMemberId: scope === 'trip' ? (assigneeMemberId ?? null) : null,
        sortOrder: base + 10 * (k + 1),
        version: 1,
        canEdit: true,
      }));
      await guardar({ ...e, items: [...e.items, ...novos] });
      await enfileirar('create', {
        create: { items: novos.map((n) => ({ id: n.id, scope: n.scope, title: n.title, category: n.category, assigneeMemberId: n.assigneeMemberId, sortOrder: n.sortOrder })) },
      });
    },

    marcar: async (itemId, packed) => {
      const e = get().estado;
      if (!e) return;
      await guardar({
        ...e,
        items: e.items.map((i) => (i.id === itemId ? { ...i, packedAt: packed ? new Date().toISOString() : null, packedByMemberId: packed ? e.meuMemberId : null } : i)),
      });
      await enfileirar('update', { itemId, packed });
    },

    editar: async (itemId, patch) => {
      const e = get().estado;
      const atual = e?.items.find((i) => i.id === itemId);
      if (!e || !atual) return;
      await guardar({ ...e, items: e.items.map((i) => (i.id === itemId ? { ...i, ...patch } : i)) });
      await enfileirar('update', { itemId, patch }, atual.version);
    },

    excluir: async (itemId) => {
      const e = get().estado;
      if (!e) return;
      await guardar({ ...e, items: e.items.filter((i) => i.id !== itemId) });
      await enfileirar('delete', { itemId });
    },

    prepararVolta: async (scope) => {
      const e = get().estado;
      if (!e) return;
      const alvo = e.items.filter((i) => i.scope === scope && i.packedAt !== null);
      await guardar({ ...e, items: e.items.map((i) => (i.scope === scope ? { ...i, packedAt: null, packedByMemberId: null } : i)) });
      for (const i of alvo) await enfileirar('update', { itemId: i.id, packed: false });
    },
  };
});

/** Versão aceita pelo servidor volta ao estado guardado (edições com versão). */
export async function aplicarAceiteDaBagagem(mutation: { entity: string; payload: unknown }, body: unknown): Promise<void> {
  if (mutation.entity !== 'baggage_item') return;
  const corpo = body as { version?: number; id?: string } | null;
  const payload = mutation.payload as { itemId?: string; patch?: unknown };
  if (!corpo || typeof corpo.version !== 'number' || !payload.itemId || !payload.patch) return;
  const s = useBagagem.getState();
  if (!s.estado) return;
  const estado = { ...s.estado, items: s.estado.items.map((i) => (i.id === payload.itemId ? { ...i, version: corpo.version! } : i)) };
  useBagagem.setState({ estado });
  const db = await localDb();
  await db.cache.set(chave(estado.tripId), estado);
}
